import { supabase, type CampanaMaster } from '../lib/supabase';
import { ResultadoCampanaInciertoError, lanzarErrorOperacionCampana } from '../utils/fondosCampanaRpc';

interface LumisialRef { readonly nombre: string; readonly ciudad: string | null }
interface LumisialConDiocesis extends LumisialRef {
  readonly diocesis_id: string | null;
  readonly provincia_eclesiastica_id: string | null;
  readonly diocesis: { readonly nombre: string } | { readonly nombre: string }[] | null;
}

function resolverNombreDiocesis(diocesis: LumisialConDiocesis['diocesis']): string {
  if (!diocesis) return '';
  return (Array.isArray(diocesis) ? diocesis[0]?.nombre : diocesis.nombre) || '';
}

export interface DistribucionLumisial {
  lumisialId: string;
  lumisialNombre: string;
  lumisialCiudad: string;
  diocesisNombre: string;
  provinciaNombre: string;
  seleccionado: boolean;
  montoAsignado: number;
}

export interface CrearCampanaMasterPayload {
  readonly campanaMasterId: string;
  readonly campana: Omit<CampanaMaster, 'id' | 'created_at' | 'creado_por_usuario_id'>;
  readonly distribucion: readonly { readonly lumisialId: string; readonly monto: number }[];
}

export interface ConsolidadoZona {
  campana: CampanaMaster;
  totalLumisiales: number;
  totalObjetivoZona: number;
  totalRecaudadoZona: number;
  porcentajeZona: number;
  lumisiales: Array<{
    campanaId: string;
    nombre: string;
    ciudad: string;
    diocesisNombre: string;
    objetivo: number;
    recaudado: number;
  }>;
}

export const campanasMasterService = {
  async getAll(): Promise<CampanaMaster[]> {
    const { data, error } = await supabase
      .from('campanas_master')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<CampanaMaster | null> {
    const { data, error } = await supabase
      .from('campanas_master')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getLumisialesElegibles(filtros: {
    provinciaEclesiasticaId?: string;
    diocesisId?: string;
  }): Promise<DistribucionLumisial[]> {
    let query = supabase
      .from('lumisiales')
      .select(`
        id,
        nombre,
        ciudad,
        provincia_eclesiastica:provincia_eclesiastica_id(id, nombre),
        diocesis:diocesis_id(id, nombre, tipo)
      `)
      .eq('activo', true)
      .order('nombre');

    if (filtros.provinciaEclesiasticaId) {
      query = query.eq('provincia_eclesiastica_id', filtros.provinciaEclesiasticaId);
    }
    if (filtros.diocesisId) {
      query = query.eq('diocesis_id', filtros.diocesisId);
    }

    const { data, error } = await query;
    if (error) throw error;

    interface LumisialRow {
      id: string;
      nombre: string;
      ciudad: string | null;
      provincia_eclesiastica: { nombre: string } | { nombre: string }[] | null;
      diocesis: { nombre: string } | { nombre: string }[] | null;
    }

    return ((data || []) as unknown as LumisialRow[]).map(l => ({
      lumisialId: l.id,
      lumisialNombre: l.nombre,
      lumisialCiudad: l.ciudad || '',
      diocesisNombre: (Array.isArray(l.diocesis) ? l.diocesis[0]?.nombre : l.diocesis?.nombre) || '',
      provinciaNombre: (Array.isArray(l.provincia_eclesiastica) ? l.provincia_eclesiastica[0]?.nombre : l.provincia_eclesiastica?.nombre) || '',
      seleccionado: false,
      montoAsignado: 0,
    }));
  },

  async crear(payload: CrearCampanaMasterPayload): Promise<CampanaMaster> {
    const c = payload.campana;
    const { data: id, error } = await supabase.rpc('crear_campana_master_distribuida', {
      p_campana_master_id: payload.campanaMasterId,
      p_nombre: c.nombre,
      p_descripcion: c.descripcion ?? null,
      p_objetivo_global: c.objetivo_global,
      p_modo_distribucion: c.modo_distribucion,
      p_fecha_inicio: c.fecha_inicio,
      p_fecha_fin: c.fecha_fin ?? null,
      p_estado: c.estado,
      p_permite_editar_objetivo_local: c.permite_editar_objetivo_local,
      p_nivel_origen: c.nivel_origen,
      p_provincia_eclesiastica_id: c.provincia_eclesiastica_id ?? null,
      p_diocesis_id: c.diocesis_id ?? null,
      p_lumisiales: payload.distribucion.map(d => d.lumisialId),
      p_montos: payload.distribucion.map(d => d.monto),
    }).then(undefined, lanzarErrorOperacionCampana);
    if (error) lanzarErrorOperacionCampana(error);
    if (typeof id !== 'string' || id !== payload.campanaMasterId) {
      throw new ResultadoCampanaInciertoError();
    }
    // La creacion pudo confirmarse aunque falle la lectura posterior: repetir mismo ID.
    try {
      const creada = await this.getById(id);
      if (creada?.id !== id) throw new ResultadoCampanaInciertoError();
      return creada;
    } catch {
      throw new ResultadoCampanaInciertoError();
    }
  },

  async update(id: string, cambios: Partial<Omit<CampanaMaster, 'id' | 'created_at'>>): Promise<CampanaMaster> {
    const { data, error } = await supabase
      .from('campanas_master')
      .update(cambios)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getCampanasMasterVisibles(opciones: {
    nivel: 'global' | 'nacional' | 'diocesano';
    provinciaEclesiasticaId?: string;
    diocesisId?: string;
    direccion: 'superior' | 'inferior';
  }): Promise<CampanaMaster[]> {
    let query = supabase
      .from('campanas_master')
      .select('*')
      .neq('estado', 'archivada')
      .order('created_at', { ascending: false });

    if (opciones.direccion === 'superior' && opciones.nivel === 'diocesano') {
      query = query.in('nivel_origen', ['global', 'nacional']);
      if (opciones.provinciaEclesiasticaId) {
        query = query.or(
          `provincia_eclesiastica_id.eq.${opciones.provinciaEclesiasticaId},provincia_eclesiastica_id.is.null`
        );
      }
    } else if (opciones.direccion === 'superior' && opciones.nivel === 'nacional') {
      query = query.eq('nivel_origen', 'global');
    } else if (opciones.direccion === 'inferior' && opciones.nivel === 'global') {
      query = query.in('nivel_origen', ['nacional', 'diocesano']);
    } else if (opciones.direccion === 'inferior' && opciones.nivel === 'nacional') {
      query = query.eq('nivel_origen', 'diocesano');
      if (opciones.provinciaEclesiasticaId) {
        query = query.eq('provincia_eclesiastica_id', opciones.provinciaEclesiasticaId);
      }
    } else {
      return [];
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getConsolidadoPorZona(id: string, opciones: {
    diocesisId?: string;
    provinciaEclesiasticaId?: string;
  }): Promise<ConsolidadoZona> {
    const [campana, campanasLocalesResult] = await Promise.all([
      this.getById(id),
      supabase
        .from('campañas')
        .select(`
          id,
          objetivo_recaudacion,
          monto_recaudado,
          lumisial:lumisial_id(
            nombre,
            ciudad,
            diocesis_id,
            diocesis:diocesis_id(nombre),
            provincia_eclesiastica_id
          )
        `)
        .eq('campana_master_id', id),
    ]);

    if (!campana) throw new Error('Campaña maestra no encontrada');
    if (campanasLocalesResult.error) throw campanasLocalesResult.error;

    type CampanaConLumisial = { id: string; objetivo_recaudacion: number | null; monto_recaudado: number | null; lumisial: LumisialConDiocesis | LumisialConDiocesis[] | null };
    let campanasLocales = (campanasLocalesResult.data || []) as unknown as CampanaConLumisial[];

    if (opciones.diocesisId) {
      campanasLocales = campanasLocales.filter(c => {
        const l = Array.isArray(c.lumisial) ? c.lumisial[0] : c.lumisial;
        return l?.diocesis_id === opciones.diocesisId;
      });
    } else if (opciones.provinciaEclesiasticaId) {
      campanasLocales = campanasLocales.filter(c => {
        const l = Array.isArray(c.lumisial) ? c.lumisial[0] : c.lumisial;
        return l?.provincia_eclesiastica_id === opciones.provinciaEclesiasticaId;
      });
    }

    const lumisiales = campanasLocales.map(c => {
      const l = Array.isArray(c.lumisial) ? c.lumisial[0] : c.lumisial;
      const diocesisNombre = l ? resolverNombreDiocesis(l.diocesis) : '';
      return {
        campanaId: c.id,
        nombre: l?.nombre || '',
        ciudad: l?.ciudad || '',
        diocesisNombre,
        objetivo: Number(c.objetivo_recaudacion || 0),
        recaudado: Number(c.monto_recaudado || 0),
      };
    });

    const totalObjetivoZona = lumisiales.reduce((s, l) => s + l.objetivo, 0);
    const totalRecaudadoZona = lumisiales.reduce((s, l) => s + l.recaudado, 0);
    const porcentajeZona = totalObjetivoZona > 0
      ? Math.round((totalRecaudadoZona / totalObjetivoZona) * 100)
      : 0;

    return {
      campana,
      totalLumisiales: lumisiales.length,
      totalObjetivoZona,
      totalRecaudadoZona,
      porcentajeZona,
      lumisiales,
    };
  },

  async actualizarObjetivosZona(
    campanaMasterId: string,
    updates: readonly { readonly campanaId: string; readonly nuevoObjetivo: number }[],
  ): Promise<void> {
    const { error } = await supabase.rpc('actualizar_objetivos_zona', {
      p_campana_master_id: campanaMasterId,
      p_campana_ids: updates.map(item => item.campanaId),
      p_objetivos: updates.map(item => item.nuevoObjetivo),
    });
    if (error) throw error;
  },

  async getConsolidado(id: string): Promise<{
    campana: CampanaMaster;
    totalLumisiales: number;
    totalRecaudado: number;
    porcentaje: number;
    lumisiales: Array<{
      nombre: string;
      ciudad: string;
      objetivo: number;
      recaudado: number;
    }>;
  }> {
    const [campana, campanasLocales] = await Promise.all([
      this.getById(id),
      supabase
        .from('campañas')
        .select(`
          nombre,
          objetivo_recaudacion,
          monto_recaudado,
          lumisial:lumisial_id(nombre, ciudad)
        `)
        .eq('campana_master_id', id),
    ]);

    if (!campana) throw new Error('Campaña maestra no encontrada');
    if (campanasLocales.error) throw campanasLocales.error;

    type CampanaConLumisialSimple = { objetivo_recaudacion: number | null; monto_recaudado: number | null; lumisial: LumisialRef | LumisialRef[] | null };
    const lumisiales = ((campanasLocales.data || []) as unknown as CampanaConLumisialSimple[]).map(c => {
      const l = Array.isArray(c.lumisial) ? c.lumisial[0] : c.lumisial;
      return {
        nombre: l?.nombre || '',
        ciudad: l?.ciudad || '',
        objetivo: Number(c.objetivo_recaudacion || 0),
        recaudado: Number(c.monto_recaudado || 0),
      };
    });

    const totalRecaudado = lumisiales.reduce((s, l) => s + l.recaudado, 0);
    const porcentaje = campana.objetivo_global > 0
      ? Math.round((totalRecaudado / campana.objetivo_global) * 100)
      : 0;

    return {
      campana,
      totalLumisiales: lumisiales.length,
      totalRecaudado,
      porcentaje,
      lumisiales,
    };
  },
};
